import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { NotificationType } from 'models/restTypes';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const NotificationTypesTable: FC<{
  notificationTypes: NotificationType[];
  isLoading: boolean;
}> = observer(({ notificationTypes, isLoading }) => {
  const { t } = useTranslation('staticData');

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    uid: { value: '', matchMode: FilterMatchMode.CONTAINS },
    name: { value: '', matchMode: FilterMatchMode.CONTAINS },
    category: { value: '', matchMode: FilterMatchMode.CONTAINS },
    timingStrategy: { value: '', matchMode: FilterMatchMode.CONTAINS },
    sendingFrequency: { value: '', matchMode: FilterMatchMode.CONTAINS },
    deliveryPolicy: { value: '', matchMode: FilterMatchMode.CONTAINS },
    publishLevel: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(({ data }: DataTableRowClickEvent) => {
    // Handle row click
    // eslint-disable-next-line no-console
    console.log(data);
  }, []);

  const activeBody = useCallback((rowData: NotificationType) => {
    return rowData.active ? 'Yes' : 'No';
  }, []);

  const sendingHourBody = useCallback((rowData: NotificationType) => {
    if (!rowData.sendingHour) return '-';
    return `${rowData.sendingHour[0]}-${rowData.sendingHour[1]}h`;
  }, []);

  const sendingDayBody = useCallback((rowData: NotificationType) => {
    return rowData.sendingDay || '-';
  }, []);

  const delayBody = useCallback((rowData: NotificationType) => {
    return rowData.delay !== null ? rowData.delay : '-';
  }, []);

  const supportedDistChannelBody = useCallback((rowData: NotificationType) => {
    if (
      !rowData.supportedDistChannel ||
      rowData.supportedDistChannel.length === 0
    ) {
      return '-';
    }

    return rowData.supportedDistChannel.join(', ');
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="NotificationTypesTable"
          showGridlines
          resizableColumns
          value={notificationTypes}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          dataKey="uid"
          emptyMessage={t('empty_message')}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          filterDisplay="row"
          globalFilterFields={[
            'uid',
            'name',
            'category',
            'timingStrategy',
            'sendingFrequency',
            'deliveryPolicy',
            'publishLevel',
          ]}
          paginator
          rows={10}
        >
          <Column field="uid" header={t('uid')} sortable filter />
          <Column field="name" header={t('name')} sortable filter />
          <Column field="category" header={t('category')} sortable filter />
          <Column
            field="timingStrategy"
            header={t('timing_strategy')}
            sortable
            filter
          />
          <Column
            field="sendingHour"
            header={t('sending_hour')}
            sortable
            body={sendingHourBody}
          />
          <Column
            field="sendingDay"
            header={t('sending_day')}
            sortable
            body={sendingDayBody}
          />
          <Column field="delay" header={t('delay')} sortable body={delayBody} />
          <Column
            field="sendingFrequency"
            header={t('sending_frequency')}
            sortable
            filter
          />
          <Column
            field="deliveryPolicy"
            header={t('delivery_policy')}
            sortable
            filter
          />
          <Column
            field="publishLevel"
            header={t('publish_level')}
            sortable
            filter
          />
          <Column
            field="supportedDistChannel"
            header={t('supported_channels')}
            sortable
            body={supportedDistChannelBody}
          />
          <Column
            field="active"
            header={t('active')}
            sortable
            body={activeBody}
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default NotificationTypesTable;
